import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import ErrorBoundary from "@/components/ErrorBoundary";
import { Toaster } from "@/components/ui/sonner";
import { PlanDisplayProvider } from "@/lib/plan";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import { CustomersList, CustomerForm } from "@/pages/Customers";
import Templates from "@/pages/Templates";
import Staff from "@/pages/Staff";
import StaffLedger from "@/pages/StaffLedger";
import ManagerLedger from "@/pages/ManagerLedger";
import Landing from "@/pages/Landing";
import Signup from "@/pages/Signup";
import SignupVerify from "@/pages/SignupVerify";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Cookies from "@/pages/Cookies";
import Dpa from "@/pages/Dpa";
import Imprint from "@/pages/Imprint";
import CookieConsent from "@/components/CookieConsent";
import Billing from "@/pages/Billing";
import Team from "@/pages/Team";
import TeamAccept from "@/pages/TeamAccept";
import Settings from "@/pages/Settings";
import Products from "@/pages/Products";  // Phase P.15.1
import Calendar from "@/pages/Calendar";
import BookingForm from "@/pages/BookingForm";
import Alerts from "@/pages/Alerts";
import Approvals from "@/pages/Approvals";
import Accounts from "@/pages/Accounts";
import Finance from "@/pages/Finance";  // Phase P.35 — payments reconciliation
import Ledger from "@/pages/Ledger";  // Phase P.37 — unified wallet page
import MyCash from "@/pages/MyCash";
import CashHold from "@/pages/CashHold";
import Reports from "@/pages/Reports";
import AtRiskCustomers from "@/pages/AtRiskCustomers";
import MileageReport from "@/pages/MileageReport";
import CustomerProfile from "@/pages/CustomerProfile";
import CustomerDuplicates from "@/pages/CustomerDuplicates";
import CustomerMerge from "@/pages/CustomerMerge";
import InvoiceDetail from "@/pages/InvoiceDetail";
import SentEmails from "@/pages/SentEmails";
import Placeholder from "@/pages/Placeholder";
import Jobs from "@/pages/Jobs";
import Onboarding from "@/pages/Onboarding";
import StaffPortal from "@/pages/StaffPortal";
import Frozen from "@/pages/Frozen";
import { AdminLogin, AdminShell, AdminDashboard } from "@/admin/AdminLayout";
import { AdminCompaniesList, AdminCompanyDetail } from "@/admin/AdminCompanies";
import AdminArchivedTrials from "@/admin/AdminArchivedTrials";
import AdminEmailTemplates from "@/admin/AdminEmailTemplates";
import AdminInvoiceTemplate from "@/admin/AdminInvoiceTemplate";
import AdminEmailConfig from "@/admin/AdminEmailConfig";

function Shell({ children }) {
  return (
    <ProtectedRoute>
      <Layout>{children}</Layout>
    </ProtectedRoute>
  );
}

// Phase 15: `/` shows the marketing landing for logged-out visitors and redirects
// signed-in managers straight to the calendar (preserves prior behavior).
function LandingOrRedirect() {
  const token = typeof window !== "undefined" ? localStorage.getItem("jb_token") : null;
  if (token) return <Navigate to="/calendar" replace />;
  return <Landing />;
}

function App() {
  return (
    <div className="App">
      <ErrorBoundary>
        <AuthProvider>
          <PlanDisplayProvider>
          <BrowserRouter>
          <CookieConsent />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/signup/verify" element={<SignupVerify />} />
            <Route path="/team/accept" element={<TeamAccept />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/cookies" element={<Cookies />} />
            <Route path="/dpa" element={<Dpa />} />
            <Route path="/imprint" element={<Imprint />} />
            <Route path="/frozen" element={<Frozen />} />

            {/* Staff portal — public, separate auth */}
            <Route path="/s/:slug" element={<StaffPortal />} />

            {/* Phase 11: Super-admin platform console (separate auth) */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<AdminShell />}>
              <Route index element={<AdminDashboard />} />
              <Route path="companies" element={<AdminCompaniesList />} />
              <Route path="companies/:id" element={<AdminCompanyDetail />} />
              <Route path="archived-trials" element={<AdminArchivedTrials />} />
              <Route path="email-templates" element={<AdminEmailTemplates />} />
              <Route path="invoice-template" element={<AdminInvoiceTemplate />} />
              <Route path="email-config" element={<AdminEmailConfig />} />
            </Route>

            {/* Manager app */}
            <Route path="/" element={<LandingOrRedirect />} />
            <Route path="/dashboard" element={<Shell><Dashboard /></Shell>} />
            <Route path="/calendar" element={<Shell><Calendar /></Shell>} />

            <Route path="/bookings/new" element={<Shell><BookingForm mode="create" /></Shell>} />
            <Route path="/bookings/:id/edit" element={<Shell><BookingForm mode="edit" /></Shell>} />

            <Route path="/alerts" element={<Shell><Alerts /></Shell>} />
            <Route path="/approvals" element={<Shell><Approvals /></Shell>} />

            <Route path="/customers" element={<Shell><CustomersList /></Shell>} />
            <Route path="/customers/duplicates" element={<Shell><CustomerDuplicates /></Shell>} />
            <Route path="/customers/merge" element={<Shell><CustomerMerge /></Shell>} />
            <Route path="/customers/new" element={<Shell><CustomerForm mode="create" /></Shell>} />
            <Route path="/customers/:id" element={<Shell><CustomerProfile /></Shell>} />
            <Route path="/customers/:id/edit" element={<Shell><CustomerForm mode="edit" /></Shell>} />

            <Route path="/templates" element={<Shell><Templates /></Shell>} />
            <Route path="/staff" element={<Shell><Staff /></Shell>} />
            <Route path="/staff/ledger" element={<Navigate to="/ledger?wallet=manager" replace />} />
            <Route path="/account/manager-ledger" element={<Navigate to="/ledger?wallet=manager" replace />} />
            <Route path="/settings" element={<Shell><Settings /></Shell>} />
            <Route path="/products" element={<Shell><Products /></Shell>} />
            <Route path="/billing" element={<Shell><Billing /></Shell>} />
            <Route path="/team" element={<Shell><Team /></Shell>} />

            <Route path="/jobs" element={<Shell><Jobs /></Shell>} />
            <Route path="/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
            <Route path="/accounts" element={<Shell><Accounts /></Shell>} />
            <Route path="/finance" element={<Shell><Finance /></Shell>} />
            <Route path="/ledger" element={<Shell><Ledger /></Shell>} />
            <Route path="/my-cash" element={<Shell><MyCash /></Shell>} />
            <Route path="/cash-hold" element={<Navigate to="/ledger?wallet=company" replace />} />
            <Route path="/reports" element={<Shell><Reports /></Shell>} />
            <Route path="/reports/at-risk-customers" element={<Shell><AtRiskCustomers /></Shell>} />
            <Route path="/reports/mileage" element={<Shell><MileageReport /></Shell>} />
            <Route path="/invoices/sent" element={<Shell><SentEmails /></Shell>} />
            <Route path="/invoices/:id" element={<Shell><InvoiceDetail /></Shell>} />

            <Route path="*" element={<Navigate to="/calendar" replace />} />
          </Routes>
        </BrowserRouter>
        <Toaster richColors position="top-right" />
        </PlanDisplayProvider>
      </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}

export default App;
